import React, { useMemo } from 'react';
import { diffLines, diffWords } from 'diff';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PromptVersion, PromptVersionStatus, VersionDiffResponse } from '@/lib/api/promptVersions';

interface PromptDiffViewProps {
  data: VersionDiffResponse;
  baseVersion?: PromptVersion | null;
  compareVersion?: PromptVersion | null;
  /** When false, hides the right-hand Base/Compared version summary cards
   *  and lets the diff content span the full width. Defaults to true so
   *  existing callers (agent versions page) render unchanged. */
  showSummaryCards?: boolean;
}

// ── Types ────────────────────────────────────────────────────────────────

interface DiffSegment {
  value: string;
  added?: boolean;
  removed?: boolean;
}

interface DiffLine {
  type: 'context' | 'added' | 'removed' | 'modified';
  /** Left side content (for context, removed, modified) */
  left?: string;
  /** Right side content (for context, added, modified) */
  right?: string;
  /** Word-level segments for modified lines — left side */
  leftSegments?: DiffSegment[];
  /** Word-level segments for modified lines — right side */
  rightSegments?: DiffSegment[];
  /** Section this line belongs to (detected from markdown headers) */
  sectionName?: string;
}

/** A group of diff lines within a section, separated by collapsed context gaps. */
interface DiffHunk {
  sectionName?: string;
  lines: DiffLine[];
}

// ── Helpers ──────────────────────────────────────────────────────────────

function statusBadgeClass(s: PromptVersionStatus): string {
  if (s === 'live') return 'bg-green-600/15 text-green-700 dark:text-green-400 border-green-600/20';
  if (s === 'staging') return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20';
  return '';
}

function statusLabel(s: PromptVersionStatus): string {
  if (s === 'live') return 'Live';
  if (s === 'staging') return 'Staging';
  return 'Draft';
}

function getCreatorInitials(version?: PromptVersion | null): string {
  const source = version?.created_by?.trim();
  if (!source) return 'SY';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (source.includes('@')) {
    return source.split('@')[0].slice(0, 2).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

// Regex to detect markdown section headers (e.g. "## 1. VOICE FUNDAMENTALS").
const SECTION_HEADER_RE = /^#{1,3}\s+(?:\d+\.\s*)?(.+)$/;

/** Number of unchanged context lines to keep around each change. */
const CONTEXT_LINES = 3;

function detectSection(line: string): string | undefined {
  const m = line.trim().match(SECTION_HEADER_RE);
  return m ? m[1].trim() : undefined;
}

/**
 * Compute a structured diff using the `diff` npm package.
 * First pass: diffLines for line-level changes.
 * Second pass: diffWords on modified line pairs for word-level highlights.
 * Then: section detection, context windowing, and hunk splitting.
 */
function computeDiff(baseText: string, compareText: string): {
  hunks: DiffHunk[];
  additions: number;
  deletions: number;
  changedSections: string[];
  hasError: boolean;
} {
  try {
    const changes = diffLines(baseText, compareText);
    const rawLines: { type: 'context' | 'added' | 'removed'; text: string }[] = [];

    for (const change of changes) {
      const lines = change.value.split('\n');
      if (lines.length > 0 && lines[lines.length - 1] === '') {
        lines.pop();
      }

      const type = change.added ? 'added' : change.removed ? 'removed' : 'context';
      for (const line of lines) {
        rawLines.push({ type, text: line });
      }
    }

    // Pair up removed+added sequences as "modified" lines with word-level diff.
    const allLines: DiffLine[] = [];
    let additions = 0;
    let deletions = 0;
    let i = 0;

    while (i < rawLines.length) {
      const entry = rawLines[i];

      if (entry.type === 'context') {
        allLines.push({ type: 'context', left: entry.text, right: entry.text });
        i++;
        continue;
      }

      if (entry.type === 'removed') {
        const removedBlock: string[] = [];
        while (i < rawLines.length && rawLines[i].type === 'removed') {
          removedBlock.push(rawLines[i].text);
          i++;
        }
        const addedBlock: string[] = [];
        while (i < rawLines.length && rawLines[i].type === 'added') {
          addedBlock.push(rawLines[i].text);
          i++;
        }

        const paired = Math.min(removedBlock.length, addedBlock.length);
        for (let j = 0; j < paired; j++) {
          const wordChanges = diffWords(removedBlock[j], addedBlock[j]);
          const leftSegments: DiffSegment[] = [];
          const rightSegments: DiffSegment[] = [];

          for (const seg of wordChanges) {
            if (seg.removed) {
              leftSegments.push({ value: seg.value, removed: true });
            } else if (seg.added) {
              rightSegments.push({ value: seg.value, added: true });
            } else {
              leftSegments.push({ value: seg.value });
              rightSegments.push({ value: seg.value });
            }
          }

          allLines.push({
            type: 'modified',
            left: removedBlock[j],
            right: addedBlock[j],
            leftSegments,
            rightSegments,
          });
          additions++;
          deletions++;
        }

        for (let j = paired; j < removedBlock.length; j++) {
          allLines.push({ type: 'removed', left: removedBlock[j] });
          deletions++;
        }
        for (let j = paired; j < addedBlock.length; j++) {
          allLines.push({ type: 'added', right: addedBlock[j] });
          additions++;
        }
        continue;
      }

      if (entry.type === 'added') {
        allLines.push({ type: 'added', right: entry.text });
        additions++;
        i++;
        continue;
      }

      i++;
    }

    // Walk through all lines and assign section names based on markdown headers.
    let currentSection: string | undefined;
    for (const line of allLines) {
      const text = line.left ?? line.right ?? '';
      const detected = detectSection(text);
      if (detected) currentSection = detected;
      if (currentSection) line.sectionName = currentSection;
    }

    // Keep a small context window around changed lines.
    const n = allLines.length;
    const keep: boolean[] = new Array(n).fill(false);

    for (let idx = 0; idx < n; idx++) {
      if (allLines[idx].type !== 'context') {
        keep[idx] = true;
        for (let j = Math.max(0, idx - CONTEXT_LINES); j < idx; j++) keep[j] = true;
        for (let j = idx + 1; j < Math.min(n, idx + CONTEXT_LINES + 1); j++) keep[j] = true;
      }
    }

    const hunks: DiffHunk[] = [];
    let currentHunk: DiffHunk | null = null;

    for (let idx = 0; idx < n; idx++) {
      if (!keep[idx]) {
        if (currentHunk && currentHunk.lines.length > 0) {
          hunks.push(currentHunk);
          currentHunk = null;
        }
        continue;
      }

      if (!currentHunk) {
        currentHunk = { sectionName: allLines[idx].sectionName, lines: [] };
      }
      currentHunk.lines.push(allLines[idx]);
    }
    if (currentHunk && currentHunk.lines.length > 0) {
      hunks.push(currentHunk);
    }

    const seenSections = new Set<string>();
    const changedSections: string[] = [];
    for (const hunk of hunks) {
      if (hunk.sectionName && !seenSections.has(hunk.sectionName)) {
        seenSections.add(hunk.sectionName);
        changedSections.push(hunk.sectionName);
      }
    }

    return { hunks, additions, deletions, changedSections, hasError: false };
  } catch (error) {
    console.error('Diff computation failed:', error);
    return { hunks: [], additions: 0, deletions: 0, changedSections: [], hasError: true };
  }
}

// ── Word-level segment rendering ─────────────────────────────────────────

function SegmentedText({
  segments,
  highlightClass,
}: {
  segments: DiffSegment[];
  highlightClass: string;
}) {
  return (
    <>
      {segments.map((seg, i) => {
        if (seg.added || seg.removed) {
          return (
            <span key={i} className={highlightClass}>
              {seg.value}
            </span>
          );
        }
        return <span key={i}>{seg.value}</span>;
      })}
    </>
  );
}

// ── Side-by-side row rendering ──────────────────────────────────────────

function SideBySideRow({ line }: { line: DiffLine }) {
  switch (line.type) {
    case 'context':
      return (
        <div className="grid grid-cols-2 divide-x divide-border">
          <div className="px-3 py-0.5 text-sm text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed">
            {line.left ?? ''}
          </div>
          <div className="px-3 py-0.5 text-sm text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed">
            {line.right ?? ''}
          </div>
        </div>
      );

    case 'removed':
      return (
        <div className="grid grid-cols-2 divide-x divide-border">
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-red-500/10 text-red-900 dark:text-red-300">
            {line.left ?? ''}
          </div>
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed" />
        </div>
      );

    case 'added':
      return (
        <div className="grid grid-cols-2 divide-x divide-border">
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed" />
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-green-500/10 text-green-900 dark:text-green-300">
            {line.right ?? ''}
          </div>
        </div>
      );

    case 'modified':
      return (
        <div className="grid grid-cols-2 divide-x divide-border">
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-red-500/10 text-red-900 dark:text-red-300">
            {line.leftSegments ? (
              <SegmentedText segments={line.leftSegments} highlightClass="bg-red-500/30 rounded-sm" />
            ) : (
              line.left ?? ''
            )}
          </div>
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-green-500/10 text-green-900 dark:text-green-300">
            {line.rightSegments ? (
              <SegmentedText segments={line.rightSegments} highlightClass="bg-green-500/30 rounded-sm" />
            ) : (
              line.right ?? ''
            )}
          </div>
        </div>
      );

    default:
      return null;
  }
}

// ── Unified (narrow viewport) row rendering ─────────────────────────────

function UnifiedRow({ line }: { line: DiffLine }) {
  switch (line.type) {
    case 'context':
      return (
        <div className="px-3 py-0.5 text-sm text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed">
          {line.left ?? ''}
        </div>
      );

    case 'removed':
      return (
        <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-red-500/10 text-red-900 dark:text-red-300">
          <span className="select-none mr-1 text-red-500/70">-</span>
          {line.left ?? ''}
        </div>
      );

    case 'added':
      return (
        <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-green-500/10 text-green-900 dark:text-green-300">
          <span className="select-none mr-1 text-green-500/70">+</span>
          {line.right ?? ''}
        </div>
      );

    case 'modified':
      return (
        <>
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-red-500/10 text-red-900 dark:text-red-300">
            <span className="select-none mr-1 text-red-500/70">-</span>
            {line.leftSegments ? (
              <SegmentedText segments={line.leftSegments} highlightClass="bg-red-500/30 rounded-sm" />
            ) : (
              line.left ?? ''
            )}
          </div>
          <div className="px-3 py-0.5 text-sm whitespace-pre-wrap font-mono leading-relaxed bg-green-500/10 text-green-900 dark:text-green-300">
            <span className="select-none mr-1 text-green-500/70">+</span>
            {line.rightSegments ? (
              <SegmentedText segments={line.rightSegments} highlightClass="bg-green-500/30 rounded-sm" />
            ) : (
              line.right ?? ''
            )}
          </div>
        </>
      );

    default:
      return null;
  }
}

function VersionSummaryCard({
  label,
  fallbackVersionNumber,
  version,
}: {
  label: string;
  fallbackVersionNumber: number;
  version?: PromptVersion | null;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            {label}
          </p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-sm font-medium px-3 py-1">
              v{version?.version ?? fallbackVersionNumber}
            </Badge>
            <Badge
              className={cn('text-xs', statusBadgeClass(version?.status ?? 'draft'))}
              variant={(version?.status ?? 'draft') === 'draft' ? 'secondary' : 'default'}
            >
              {statusLabel(version?.status ?? 'draft')}
            </Badge>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        <Avatar className="h-8 w-8 border border-border/70">
          <AvatarImage src={version?.created_by_avatar_url || undefined} alt={version?.created_by || 'System'} />
          <AvatarFallback className="text-[11px] font-medium">
            {getCreatorInitials(version)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {version?.created_by || 'System'}
          </p>
          <p className="text-caption text-muted-foreground">
            {version?.created_at ? new Date(version.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }) : 'Timestamp unavailable'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Short Summary
          </p>
          <p className="mt-1 text-sm leading-relaxed text-foreground">
            {version?.summary || 'No short summary available for this version yet.'}
          </p>
        </div>

        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Detailed Summary
          </p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {version?.full_summary || 'No detailed summary available for this version yet.'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────

const PromptDiffView: React.FC<PromptDiffViewProps> = ({
  data,
  baseVersion,
  compareVersion,
  showSummaryCards = true,
}) => {
  const diff = useMemo(() => {
    if (data.is_latest || data.is_first_version || (!data.base_text && !data.compare_text)) {
      return null;
    }
    return computeDiff(data.base_text, data.compare_text);
  }, [data.base_text, data.compare_text, data.is_latest, data.is_first_version]);

  // ── Special states ──────────────────────────────────────────────────

  if (data.is_latest) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">
          This is the latest version — no newer version to compare against.
        </p>
      </div>
    );
  }

  if (data.is_first_version && (!diff || diff.hunks.length === 0)) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">
          This is the first version — no previous version to compare against.
        </p>
      </div>
    );
  }

  if (diff?.hasError) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">
          Unable to render this version diff.
        </p>
      </div>
    );
  }

  if (!diff || diff.hunks.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">
          No changes between these versions.
        </p>
      </div>
    );
  }

  // ── Diff view ───────────────────────────────────────────────────────

  return (
    <div
      className={cn(
        'grid gap-4 xl:items-start',
        showSummaryCards && 'xl:grid-cols-[minmax(0,1fr)_320px]',
      )}
    >
      <div className="space-y-4 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-sm font-medium px-3 py-1">
              v{data.base_version}
            </Badge>
            <Badge
              className={cn('text-xs', statusBadgeClass(data.base_status))}
              variant={data.base_status === 'draft' ? 'secondary' : 'default'}
            >
              {statusLabel(data.base_status)}
            </Badge>
            <span className="text-muted-foreground text-sm">&rarr;</span>
            <Badge variant="outline" className="text-sm font-medium px-3 py-1">
              v{data.compare_version}
            </Badge>
            <Badge
              className={cn('text-xs', statusBadgeClass(data.compare_status))}
              variant={data.compare_status === 'draft' ? 'secondary' : 'default'}
            >
              {statusLabel(data.compare_status)}
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-sm font-mono">
            <span className="text-green-600 dark:text-green-400">+{diff.additions}</span>
            <span className="text-red-600 dark:text-red-400">-{diff.deletions}</span>
          </div>
        </div>

        {/* Section jump nav */}
        {diff.changedSections.length >= 2 && (
          <div className="flex flex-wrap gap-1.5">
            {diff.changedSections.map((section) => (
              <button
                key={section}
                type="button"
                className="text-xs px-2.5 py-1 rounded-full border border-border bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                onClick={() => {
                  const el = document.getElementById(`diff-section-${section}`);
                  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {section}
              </button>
            ))}
          </div>
        )}

        {/* Column headers for side-by-side */}
        <div className="hidden lg:grid grid-cols-2 divide-x divide-border border border-border rounded-t-lg bg-muted/30">
          <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
            v{data.base_version}
          </div>
          <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
            v{data.compare_version}
          </div>
        </div>

        {/* Hunks */}
        <div className="border border-border rounded-lg lg:rounded-t-none lg:border-t-0 overflow-hidden">
          {diff.hunks.map((hunk, hunkIdx) => {
            const prevSection = hunkIdx > 0 ? diff.hunks[hunkIdx - 1].sectionName : undefined;
            const showSectionDivider = hunk.sectionName && hunk.sectionName !== prevSection;

            return (
              <React.Fragment key={hunkIdx}>
                {/* Collapsed separator between hunks */}
                {hunkIdx > 0 && (
                  <div className="flex items-center justify-center py-1.5 bg-muted/20 border-y border-border">
                    <span className="text-xs text-muted-foreground tracking-widest">
                      &middot;&middot;&middot;
                    </span>
                  </div>
                )}

                {/* Section label */}
                {showSectionDivider && (
                  <div
                    id={`diff-section-${hunk.sectionName}`}
                    className="px-3 py-2 bg-muted/40 border-b border-border"
                  >
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {hunk.sectionName}
                    </span>
                  </div>
                )}

                {/* Rows — side-by-side on lg+, unified on smaller */}
                <div>
                  {hunk.lines.map((line, lineIdx) => (
                    <React.Fragment key={lineIdx}>
                      {/* Side-by-side (lg+) */}
                      <div className="hidden lg:block">
                        <SideBySideRow line={line} />
                      </div>
                      {/* Unified (< lg) */}
                      <div className="lg:hidden">
                        <UnifiedRow line={line} />
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {showSummaryCards && (
        <aside className="space-y-3 xl:sticky xl:top-4">
          <VersionSummaryCard
            label="Base Version"
            fallbackVersionNumber={data.base_version}
            version={baseVersion}
          />
          <VersionSummaryCard
            label="Compared Version"
            fallbackVersionNumber={data.compare_version}
            version={compareVersion}
          />
        </aside>
      )}
    </div>
  );
};

export default PromptDiffView;
